import { Stack, Box, Grid, Tooltip } from '@mui/material';
import { ColumnDefinition, GridProps, ColumnType } from '@sm-dashboard/types';
import { useMemo, useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { motion } from 'framer-motion';
import { ExpansionToggle } from '../expanded/ExpansionToggle';
import { actionsRender } from '../actions/Actions';
import { renderCell } from './renderCell';
import { getRowStyles, rowStableKey, separateActionsByLevel, separateColumnsByDisplayLevel } from '../helpFunctions';
import AnimatedExpandedContent from './AnimatedExpandedContent';
import { Icon, IconButton } from '@sm-dashboard/ui-components';
import { alpha, SxProps, Theme } from '@mui/material/styles';

const MOBILE_MAX_HEIGHT_VH = 80;
const MOBILE_ROW_ESTIMATE_PX = 112;
const MOBILE_VIRTUAL_LOAD_THRESHOLD = 8;
const MOBILE_ROW_CHIP_HEIGHT = 27;
const LONG_PRESS_MS = 500;
const LONG_PRESS_MOVE_THRESHOLD_PX = 10;

/**
 * Scroll-end padding inside the table list so the last row can clear the floating
 * bottom nav. Keep in sync with `MOBILE_BOTTOM_NAV_INSET` in
 * `apps/frontend/src/layouts/mobileLayoutConstants.ts` (80px pill + 10px gap).
 */
const MOBILE_BOTTOM_NAV_SCROLL_PADDING = 'calc(90px + env(safe-area-inset-bottom, 0px))';

type MobileGridViewProps<T, U> = GridProps<T, U> & {
  selectedRows: number[];
  onRowSelectionToggle: (index: number) => void;
  onRowSelectionSelect?: (index: number) => void;
};

const isNewRow = (row: unknown): boolean => Boolean((row as any)?.isNew);

const isTextLikeColumn = (column: GridProps<any, any>['columns'][number]): boolean => {
  const type = column.type;
  return (
    type === undefined ||
    type === 'text' ||
    type === 'number' ||
    type === 'date' ||
    type === 'dateTime' ||
    type === 'phone' ||
    type === 'email' ||
    type === 'doc' ||
    type === 'richText'
  );
};

const MobileGridView = <T, U>({ ...props }: MobileGridViewProps<T, U>) => {
  const {
    data,
    columns,
    actions,
    components,
    responsive,
    expanded,
    tableRef,
    contentFillsScrollParent,
    onRowClick,
    mobileVirtualInfinite,
    onSort,
    sortField,
    sortDirection,
    sortable: gridSortable,
    selectable,
    mobileBulkSelectMode = false,
    onMobileBulkSelectModeChange,
    selectedRows: selectedRowsProp,
    onRowSelectionToggle,
    onRowSelectionSelect
  } = props;
  const selectedRows = selectedRowsProp ?? [];
  const isBulkSelectActive = Boolean(selectable && mobileBulkSelectMode);
  const rows = data ?? [];
  const useVirtualList = Boolean(contentFillsScrollParent && rows.length > 0);
  const { mobileStyle } = responsive || {};
  const { onExpand, renderExpanded, childRowsKey, panelLayout } = expanded || {};
  const [open, setOpen] = useState<number | null>(null);

  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [lockedIndex, setLockedIndex] = useState<number | null>(null);
  const [lockedTop, setLockedTop] = useState<number>(0);
  const enterWaitIndexRef = useRef<number | null>(null);
  const exitWaitIndexRef = useRef<number | null>(null);
  const lastLockedVisualTopRef = useRef<number>(0);
  const initialRowOffsetTopRef = useRef<number>(0);
  const initialContainerScrollTopRef = useRef<number>(0);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTriggeredRef = useRef(false);
  const touchStartPosRef = useRef<{ x: number; y: number } | null>(null);

  const lockRowPosition = (index: number) => {
    const rowEl = rowRefs.current[index];
    const containerEl = tableRef?.current;
    if (!rowEl || !containerEl) return;
    const rowTop = rowEl.getBoundingClientRect().top;
    const containerTop = containerEl.getBoundingClientRect().top;
    setLockedTop(rowTop - containerTop);
    lastLockedVisualTopRef.current = rowTop;
    initialRowOffsetTopRef.current = rowEl.offsetTop;
    initialContainerScrollTopRef.current = containerEl.scrollTop;
    setLockedIndex(index);
  };

  const separatedFields = useMemo(() => separateColumnsByDisplayLevel(columns, true), [columns]);
  const firstFields = useMemo(() => separatedFields['first'], [separatedFields]);
  const primaryFields = useMemo(() => separatedFields['primary'], [separatedFields]);
  const primaryEndIndex = useMemo(
    () => primaryFields?.findIndex(col => col.mobileAlign === 'end') ?? -1,
    [primaryFields]
  );
  const [primaryActions, regularActions] = useMemo(() => separateActionsByLevel(actions || [], true), [actions]);

  const usesMobileSlotLayout = useMemo(() => columns.some(col => col.mobileSlot && col.show !== false), [columns]);

  const mobileSlotColumns = useMemo(() => {
    const visible = columns.filter(col => col.show !== false);
    return {
      name: visible.find(col => col.mobileSlot === 'name'),
      date: visible.find(col => col.mobileSlot === 'date'),
      tag: visible.find(col => col.mobileSlot === 'tag'),
      amount: visible.find(col => col.mobileSlot === 'amount')
    };
  }, [columns]);

  const isColumnSortable = useCallback(
    (column: GridProps<T, U>['columns'][number]): boolean => {
      const allowedTypes: Array<ColumnType> = ['text', 'number', 'amount', 'date', 'dateTime'];
      if (column.type && !allowedTypes.includes(column.type)) {
        return false;
      }
      if (column.type === 'mailingApproval') {
        return false;
      }

      if (data && data.length > 0) {
        let sampleValue: any;
        if (column.valueGetter) {
          sampleValue = column.valueGetter(data[0]);
        } else {
          sampleValue = (data[0] as any)[column.field];
        }
        if (typeof sampleValue === 'boolean') {
          return false;
        }
      }

      if (gridSortable === false) {
        return false;
      }
      if (gridSortable === true) {
        return column.sortable !== false;
      }
      return column.sortable === true;
    },
    [data, gridSortable]
  );

  const handleSortClick = (field: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    if (!onSort) return;

    if (sortField === field) {
      if (sortDirection === 'asc') {
        onSort(field, 'desc');
      } else if (sortDirection === 'desc') {
        onSort(field, null);
      } else {
        onSort(field, 'asc');
      }
    } else {
      onSort(field, 'asc');
    }
  };

  const getSortIcon = (field: string) => {
    if (sortField === field && sortDirection === 'asc') {
      return <Icon icon="arrowDownward" color="inherit" />;
    }
    if (sortField === field && sortDirection === 'desc') {
      return <Icon icon="arrowUpward" color="inherit" />;
    }
    return (
      <Box sx={{ opacity: 0.5, display: 'flex' }}>
        <Icon icon="unfoldMore" color="inherit" />
      </Box>
    );
  };

  const sortButton = (field: string) => (
    <Tooltip title="מיון">
      <IconButton
        variant="text"
        color="secondary"
        shape="circle"
        size="small"
        aria-label="מיון"
        onClick={e => handleSortClick(field, e)}
      >
        {getSortIcon(field)}
      </IconButton>
    </Tooltip>
  );

  /** Stretch the header over the row's `p: 2` so a tap anywhere on the row toggles it; layout is unchanged. */
  const ROW_HIT_AREA_SX = { m: -2, p: 2, cursor: 'pointer' } as const;

  const baseStyle = {
    boxShadow: 'none',
    backgroundColor: 'background.default',
    borderBottom: (theme: any) => `2px solid ${alpha(theme.palette.text.primary, 0.05)}`,
    p: 2
  };

  const getChildRows = (row: T, childRowsKey?: keyof T) => {
    if (childRowsKey) {
      return row[childRowsKey as keyof T] as U[];
    }
    return [row];
  };

  const handleRowToggle = (row: T, index: number) => {
    lockRowPosition(index);
    const nextOpen = open === index ? null : index;
    enterWaitIndexRef.current = nextOpen;
    exitWaitIndexRef.current = open;
    setOpen(nextOpen);
    onExpand?.(row);
  };

  useEffect(() => {
    if (open === null && lockedIndex !== null) {
      setLockedIndex(null);
    }
  }, [open, lockedIndex]);

  useEffect(() => {
    if (isBulkSelectActive) {
      setOpen(null);
      setLockedIndex(null);
    }
  }, [isBulkSelectActive]);

  const clearLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearLongPress(), [clearLongPress]);

  const handleLongPressStart = useCallback(
    (index: number, clientX: number, clientY: number) => {
      if (!selectable || isBulkSelectActive || !onMobileBulkSelectModeChange) return;
      longPressTriggeredRef.current = false;
      touchStartPosRef.current = { x: clientX, y: clientY };
      clearLongPress();
      longPressTimerRef.current = setTimeout(() => {
        longPressTriggeredRef.current = true;
        onMobileBulkSelectModeChange(true);
        (onRowSelectionSelect ?? onRowSelectionToggle)(index);
      }, LONG_PRESS_MS);
    },
    [
      selectable,
      isBulkSelectActive,
      onMobileBulkSelectModeChange,
      onRowSelectionSelect,
      onRowSelectionToggle,
      clearLongPress
    ]
  );

  const handleLongPressMove = useCallback(
    (clientX: number, clientY: number) => {
      if (!touchStartPosRef.current) return;
      const dx = Math.abs(clientX - touchStartPosRef.current.x);
      const dy = Math.abs(clientY - touchStartPosRef.current.y);
      if (dx > LONG_PRESS_MOVE_THRESHOLD_PX || dy > LONG_PRESS_MOVE_THRESHOLD_PX) {
        clearLongPress();
      }
    },
    [clearLongPress]
  );

  const handleLongPressEnd = useCallback(() => {
    clearLongPress();
    touchStartPosRef.current = null;
  }, [clearLongPress]);

  const rowLongPressHandlers = (index: number) =>
    selectable
      ? {
          onPointerDown: (e: React.PointerEvent) => {
            if (e.pointerType === 'mouse' && e.button !== 0) return;
            handleLongPressStart(index, e.clientX, e.clientY);
          },
          onPointerMove: (e: React.PointerEvent) => handleLongPressMove(e.clientX, e.clientY),
          onPointerUp: handleLongPressEnd,
          onPointerCancel: handleLongPressEnd,
          onContextMenu: (e: React.MouseEvent) => {
            if (!isBulkSelectActive) {
              e.preventDefault();
            }
          }
        }
      : {};

  const handleRowPrimaryAction = (row: T, index: number) => {
    if (longPressTriggeredRef.current) {
      longPressTriggeredRef.current = false;
      return;
    }
    if (isBulkSelectActive) {
      onRowSelectionToggle?.(index);
      return;
    }
    if (onRowClick) {
      onRowClick(row);
      return;
    }
    handleRowToggle(row, index);
  };

  const isRowSelected = (index: number) => selectedRows.includes(index);

  const selectionRowStyle = (index: number) =>
    isRowSelected(index)
      ? {
          bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.08),
          borderInlineStart: (theme: Theme) => `3px solid ${theme.palette.primary.main}`
        }
      : {};

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableRef?.current ?? null,
    estimateSize: () => MOBILE_ROW_ESTIMATE_PX,
    overscan: 8,
    getItemKey: index => rowStableKey(rows[index], index),
    enabled: useVirtualList
  });

  useLayoutEffect(() => {
    if (!useVirtualList || open === null) {
      return;
    }
    const el = rowRefs.current[open];
    if (el) {
      virtualizer.measureElement(el);
    }
  }, [open, useVirtualList, virtualizer, rows.length]);

  useEffect(() => {
    if (!useVirtualList || !mobileVirtualInfinite) {
      return;
    }
    const range = virtualizer.range;
    if (!range) {
      return;
    }
    if (
      mobileVirtualInfinite.hasMore &&
      !mobileVirtualInfinite.isLoadingMore &&
      range.endIndex >= rows.length - MOBILE_VIRTUAL_LOAD_THRESHOLD
    ) {
      mobileVirtualInfinite.onLoadMore();
    }
  }, [
    virtualizer.range,
    rows.length,
    useVirtualList,
    mobileVirtualInfinite?.hasMore,
    mobileVirtualInfinite?.isLoadingMore,
    mobileVirtualInfinite?.onLoadMore,
    mobileVirtualInfinite
  ]);

  const mobileCellBox = (row: T, column: GridProps<T, U>['columns'][number]) => (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        ...(isNewRow(row) && isTextLikeColumn(column)
          ? {
              fontWeight: 600,
              '& .MuiTypography-root': { fontWeight: 600 },
              '& a': { fontWeight: 600 }
            }
          : {})
      }}
    >
      {renderCell(row, column, undefined, { preferMobileOverride: true })}
    </Box>
  );

  const renderMobileSlotCell = (row: T, column?: ColumnDefinition<T>) => {
    if (!column) return null;
    return (
      <Box
        sx={{
          minWidth: 0,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          ...(isNewRow(row) && isTextLikeColumn(column)
            ? {
                fontWeight: 600,
                '& .MuiTypography-root': { fontWeight: 600 },
                '& a': { fontWeight: 600 }
              }
            : {}),
          '& .MuiChip-root': {
            maxWidth: '100%',
            height: MOBILE_ROW_CHIP_HEIGHT,
            '& .MuiChip-label': {
              px: 1,
              lineHeight: `${MOBILE_ROW_CHIP_HEIGHT}px`
            }
          }
        }}
      >
        {renderCell(row, column, undefined, { preferMobileOverride: true })}
      </Box>
    );
  };

  const renderExpansionToggle = (index: number) => (
    <motion.div animate={{ rotate: open === index ? 180 : 0 }} transition={{ duration: 0.2, ease: 'easeInOut' }}>
      <ExpansionToggle isExpanded={open === index} />
    </motion.div>
  );

  const renderMobileSlotHeader = (row: T, index: number) => {
    const { name: nameCol, date: dateCol, tag: tagCol, amount: amountCol } = mobileSlotColumns;
    const statusColumn = columns.find(col => col.field === 'isCreditReceived' && col.show !== false) as
      | ColumnDefinition<T>
      | undefined;

    return (
      // The whole row toggles (incl. its padding); only the primary action buttons stop the click.
      <Box onClick={() => handleRowPrimaryAction(row, index)} sx={{ ...ROW_HIT_AREA_SX, overflow: 'hidden' }}>
        <Stack spacing={0.75}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              width: '100%'
            }}
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              {renderMobileSlotCell(row, nameCol)}
            </Box>
            <Box
              component="p"
              sx={{
                typography: 'body2',
                m: 0,
                flexShrink: 0,
                color: 'text.secondary',
                whiteSpace: 'nowrap',
                fontSize: '13px'
              }}
            >
              {dateCol ? renderCell(row, dateCol) : null}
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              width: '100%'
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
              {renderMobileSlotCell(row, tagCol)}
            </Box>
            <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1, direction: 'ltr' }}>
              {amountCol
                ? renderMobileSlotCell(row, amountCol)
                : statusColumn && renderMobileSlotCell(row, statusColumn)}
              {primaryActions && primaryActions.length > 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={e => e.stopPropagation()}>
                  {actionsRender(primaryActions, row)}
                </Box>
              ) : null}
              {!isBulkSelectActive && !onRowClick ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>{renderExpansionToggle(index)}</Box>
              ) : null}
            </Box>
          </Box>
        </Stack>
      </Box>
    );
  };

  const renderHeader = (row: T, index: number) => {
    if (usesMobileSlotLayout) {
      return renderMobileSlotHeader(row, index);
    }

    return (
      <Box onClick={() => handleRowPrimaryAction(row, index)} sx={ROW_HIT_AREA_SX}>
        <Stack spacing={1} sx={{ width: '100%', overflow: 'hidden' }}>
          <Grid container flexWrap="nowrap" alignItems="center" sx={{ width: '100%' }}>
            <Grid item container gap={1} sx={{ flex: 1, minWidth: 0 }} wrap="nowrap">
              {firstFields?.map((column, key) => (
                <Grid item key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                  {mobileCellBox(row, column)}
                  {isColumnSortable(column) && onSort && index === 0 && sortButton(column.field as string)}
                </Grid>
              ))}
            </Grid>
            {primaryActions && primaryActions.length > 0 && (
              <Grid item sx={{ flex: '0 0 auto', ml: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                {actionsRender(primaryActions, row)}
              </Grid>
            )}
            {!isBulkSelectActive && !onRowClick ? (
              <Grid item sx={{ flex: '0 0 auto', mx: 2, ml: 3, display: 'flex', alignItems: 'center' }}>
                {renderExpansionToggle(index)}
              </Grid>
            ) : null}
          </Grid>
          {primaryFields && primaryFields.length > 0 && (
            // Full width (the -16px offsets the items' own margin) so end-aligned columns reach the edge.
            <Grid container flexWrap={'nowrap'} sx={{ ml: '-16px !important', width: 'calc(100% + 16px)' }}>
              {primaryFields?.map((column, key) => (
                <Grid
                  item
                  key={key}
                  sx={{
                    ml: key === primaryEndIndex ? 'auto' : 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    minWidth: 0
                  }}
                >
                  {mobileCellBox(row, column)}
                  {isColumnSortable(column) && onSort && index === 0 && sortButton(column.field as string)}
                </Grid>
              ))}
            </Grid>
          )}
        </Stack>
      </Box>
    );
  };

  const renderExpandedContent = (row: T, index: number) =>
    isBulkSelectActive || onRowClick ? null : (
      <AnimatedExpandedContent
        row={row}
        index={index}
        isOpen={open === index}
        lockedIndex={lockedIndex}
        tableRef={tableRef}
        rowRefs={rowRefs}
        lastLockedVisualTopRef={lastLockedVisualTopRef}
        enterWaitIndexRef={enterWaitIndexRef}
        exitWaitIndexRef={exitWaitIndexRef}
        setLockedIndex={setLockedIndex}
        getChildRows={getChildRows}
        childRowsKey={childRowsKey}
        renderExpanded={renderExpanded}
        columns={columns}
        regularActions={regularActions}
        panelLayout={panelLayout}
      />
    );

  const rowListItemSx = (row: T, index: number) =>
    [
      getRowStyles(row, baseStyle, mobileStyle),
      selectionRowStyle(index),
      {
        overflowAnchor: open === index ? 'auto' : 'none',
        position: lockedIndex === index ? 'sticky' : 'static',
        top: lockedIndex === index ? `${lockedTop}px` : undefined,
        zIndex: lockedIndex === index ? 1 : 'auto'
      }
    ] as SxProps<Theme>;

  const virtualRowItemSx = (row: T, index: number, start: number) =>
    [
      getRowStyles(row, baseStyle, mobileStyle),
      selectionRowStyle(index),
      {
        overflowAnchor: open === index ? 'auto' : 'none',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        transform: `translateY(${start}px)`
      }
    ] as SxProps<Theme>;

  const assignRowRef = (index: number, el: HTMLDivElement | null) => {
    rowRefs.current[index] = el;
  };

  const renderRowContent = (row: T, index: number) => (
    <>
      {renderHeader(row, index)}
      {renderExpandedContent(row, index)}
    </>
  );

  const listFooter = mobileVirtualInfinite?.isLoadingMore ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
      <Box component="p" sx={{ typography: 'body1', m: 0, textAlign: 'center' }}>
        <b>טוען...</b>
      </Box>
    </Box>
  ) : null;

  return (
    <Box
      sx={{
        width: '100%',
        marginBottom: 0.5,
        overflowAnchor: 'none',
        ...(contentFillsScrollParent ? {} : { maxHeight: `${MOBILE_MAX_HEIGHT_VH}vh` })
      }}
    >
      {components?.AddButton && <Box sx={{ m: 2, width: '100%' }}>{components.AddButton}</Box>}
      {useVirtualList ? (
        <Box sx={{ height: virtualizer.getTotalSize(), width: '100%', position: 'relative' }}>
          {virtualizer.getVirtualItems().map(virtualRow => {
            const row = rows[virtualRow.index];
            const index = virtualRow.index;
            return (
              <Box
                key={String(virtualRow.key)}
                data-index={index}
                ref={(el: HTMLDivElement | null) => {
                  virtualizer.measureElement(el);
                  assignRowRef(index, el);
                }}
                sx={virtualRowItemSx(row, index, virtualRow.start)}
                {...rowLongPressHandlers(index)}
              >
                {renderRowContent(row, index)}
              </Box>
            );
          })}
        </Box>
      ) : (
        rows.map((row, index) => (
          <Box
            key={rowStableKey(row, index)}
            sx={rowListItemSx(row, index)}
            ref={(el: HTMLDivElement | null) => assignRowRef(index, el)}
            {...rowLongPressHandlers(index)}
          >
            {renderRowContent(row, index)}
          </Box>
        ))
      )}
      {listFooter}
      {/* Inside-list clearance: scrolls with rows so the last item isn't covered by the nav. */}
      <Box
        aria-hidden
        sx={{
          height: MOBILE_BOTTOM_NAV_SCROLL_PADDING,
          width: '100%',
          flexShrink: 0,
          pointerEvents: 'none'
        }}
      />
    </Box>
  );
};

export default MobileGridView;
